import React from "react";
import PageAncestor from "core/common/page/pageAncestor";
import mdEntityService from "core/metamodel/mdEntityService";
import {preSave, IDomainObject, lovValue} from "core/form/formUtils";
import Toolmenu from "core/components/toolmenu/toolmenu";
import {ErrorComp} from "core/components/errorComp/errorComp";
import PriceComponentForm from "product/component/priceComponentForm";
import ConfirmationDialog from "core/components/confirmationDialog/confirmationDialog";
import {MODULE, PERMISSION, PageProps} from "core/metamodel/metaConstant";
import {observer} from "mobx-react";
import {WithDeleteDialogZzFlatButton, ZzFlatButton} from "core/components/buttons/buttons";
import productService from "product/productService";
import {PRICECOMPLEVEL, PriceComponent} from "product/productDomain";
import {action, ObservableMap} from "mobx";
import MdEntity from "core/metamodel/mdEntity";
import uiService from "core/common/service/uiService";
import {History} from "react-router";
import {ValidationMessage} from "core/common/domain/commonDomain";
import {loc} from "../../core/common/config/locales";

@observer
export default class PriceComponentDetail extends PageAncestor<PageProps, undefined> {

  static module = MODULE.product;
  static title = 'Price';
  static icon = 'dollar';
  static protectedPage = true;



  static fetchData(routerParams, query) {
    console.log("PriceComponentDetail#fetchData(%O)", query);

    let metadataPromise = mdEntityService.fetchEntityMetadata(['PriceComponent']);

    let priceComponentPromise: Promise<PriceComponent>;
    if (routerParams.id === 'new') {
      let {partyId, partyLabel, ...queryOther} = query;
      let party  = (partyId) ? {value: partyId, label: partyLabel} : null;
      priceComponentPromise = Promise.resolve(new PriceComponent({$new: true, party, ...queryOther}));
    } else if (routerParams.id === 'copy') {
      priceComponentPromise = productService.readPriceComponent(query.copyId)
        .then(priceComponentObject => {
          priceComponentObject.cleanOnCopy();
          return priceComponentObject;
        })
    } else {
      priceComponentPromise = productService.readPriceComponent(routerParams.id);
    }

    return Promise.all([metadataPromise, priceComponentPromise])
      .then(action("PriceComponentDetail-fetchData-callback", ([entityMap, priceComponentObject]: [ObservableMap<MdEntity>, PriceComponent]) => {
        productService.priceComponentObject = priceComponentObject.initAfterRead(routerParams.id === 'new');
      }));
  }



  componentWillUnmount() {
    productService.priceComponentObject = null;
  }


  customValidate = (priceComponentObject: PriceComponent) => {
    let errors: ValidationMessage[] = [];

    switch(lovValue(priceComponentObject.priceLevel)) {
      case PRICECOMPLEVEL.PRODUCT:
        if (priceComponentObject.vehicle || priceComponentObject.party) {
          errors.push({message: "Price level PRODUCT must not have customer or vehicle filled in"})
        }
        break;
      case PRICECOMPLEVEL.PRODUCTCUSTOMER:
        if (priceComponentObject.vehicle || !priceComponentObject.party) {
          errors.push({message: "Price level PRODUCTCUSTOMER must have customer but not vehicle filled in"})
        }
        break;
      case PRICECOMPLEVEL.PRODUCTVEHICLE:
        if (!priceComponentObject.vehicle ||priceComponentObject.party) {
          errors.push({message: "Price level PRODUCTVEHICLE must have vehicle but not customer filled in"})
        }
        break;
    }

    return errors;
  };


  findRootObject() {
    return productService.priceComponentObject;
  }

  @action onSave = (evt) => {
    console.log('onSave ');

    uiService.showToolbarActionFlag();

    let result = preSave(productService.priceComponentObject, this.customValidate);
    if (!result) {
      return;
    }

    productService.priceComponentSave(productService.priceComponentObject)
      .then( action("priceComponentSave-callback", (priceComponentJson: IDomainObject) => {

        let wasNew = productService.priceComponentObject.cleanAfterSave(priceComponentJson);
        if(wasNew) {
          this.context.router.replaceWith('priceComponentDetail', {id: productService.priceComponentObject.priceComponentId});
        }
        uiService.hideToolbarActionFlag();
      }), error => {
        productService.priceComponentObject.$errors = error.getMessages();
        productService.priceComponentObject.getForm("PriceComponentForm").$open = true;
      });
  };


  @action onDelete = (evt) => {
    console.log('onDelete');

    productService.deletePriceComponent(productService.priceComponentObject.priceComponentId)
      .then(response => {
        this.goBackOrHome();
      }, error => {
        productService.priceComponentObject.$errors = error.getMessages();
      });
  };


  onAdd = (evt) => {
    console.log('onAdd');
    let query = {priceCurrency: 'EUR'};
    this.context.router.transitionTo("priceComponentDetail", {id: 'new'}, query);
  };

  onCopy = (evt) => {
    console.log('onCopy');
    this.context.router.transitionTo("priceComponentDetail", {id: 'copy'}, {copyId: productService.priceComponentObject.priceComponentId});
  };




  render() {

    let {
      query
      } = this.props;

    console.debug('%c PriceComponentDetail render query %O', 'background-color: yellow', this.props.query);


    return (
      <main className="main-content" style={{display: 'flex', flexDirection: 'column'}} >
        {this._createToolMenu(productService.priceComponentObject, query.gridLocation)}
        <ErrorComp messages={productService.priceComponentObject.$errors} />
        <ConfirmationDialog ref={this.dirtyDialogRefHandler}/>
        <ConfirmationDialog ref={this.deleteDialogRefHandler} okButtonText={loc.i18n`Yes`} cancelButtonText={loc.i18n`No`}/>

        <form style={{marginTop: 10}}>
          <div className="row">
            <div className="col-xs-12">
              <PriceComponentForm dataObject={productService.priceComponentObject}
                                  rootObject={productService.priceComponentObject}
                                  entity={mdEntityService.getEntity("PriceComponent")}
                                  entities={mdEntityService.entityMap}/>
            </div>
          </div>
        </form>
      </main>
    );
  }




  _createToolMenu(priceComponentObject, gridLocation) {
    return (
      <Toolmenu chainGridLocation={gridLocation} backAction={this.onBack} backDisabled={History.length <= 1} historize entityName="PriceComponent" entityId={priceComponentObject.priceComponentId}>
        <ZzFlatButton onClick={this.onSave} disabled={!priceComponentObject.$dirty} label="Save" permission={priceComponentObject.$new ? PERMISSION.New : PERMISSION.Update}/>
        <ZzFlatButton onClick={this.onAdd} disabled={priceComponentObject.$new} label="New" permission={PERMISSION.New}/>
        <ZzFlatButton onClick={this.onCopy} disabled={priceComponentObject.$new} label="Copy" permission={PERMISSION.New}/>
        <WithDeleteDialogZzFlatButton entityName="PriceComponent" pageAncestor={this} onClick={this.onDelete} disabled={priceComponentObject.$new} label="Delete" permission={PERMISSION.Delete}/>

      </Toolmenu>
    );
  }


}






// WEBPACK FOOTER //
// ./src/product/page/priceComponentDetail.tsx